import { Kpi } from "@acto/taskpane/core/domain";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { CurrencyCode } from "@acto/taskpane/core/utils/currencyCode";
import { currencyPattern, formatCurrencyValue, stripNbsp } from "@acto/taskpane/core/utils/currencyPattern";

const DEFAULT_LOCALE = "de-DE";

export const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

// "Hauptstraße 1, 10115 Berlin" — postal address on one line; null when no part is known.
export const formatCustomerAddress = (customer: Pick<Customer, "street" | "postcode" | "city">): string | null => {
  const cityLine = [customer.postcode, customer.city].filter(Boolean).join(" ");
  const parts = [customer.street, cityLine].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

// `new Date("YYYY-MM-DD")` parses as UTC midnight per ECMA-262, while `new Date()`
// is a UTC instant. Anchoring `now` to local-wall-clock midnight (expressed as a
// UTC instant via Date.UTC) puts both sides at the same day boundary, otherwise
// users east of UTC see negative day counts between local and UTC midnight.
export const daysSince = (value: string | Date, now = new Date()): number => {
  const last = typeof value === "string" ? new Date(value) : value;
  const nowUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

  return Math.floor((nowUtc - last.getTime()) / 86_400_000);
};

// All date formatters are UTC-pinned: the backend's LocalDate values arrive as `YYYY-MM-DD`, which
// `new Date()` parses as UTC midnight. Pinning to UTC keeps the rendered day stable regardless of the
// viewer's timezone (otherwise users east/west of UTC see off-by-one days near midnight).
const toUtcDate = (value: string | Date): Date | null => {
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date;
};

// The single sanctioned date constructor for field-sales-2. Every visible date flows through one of
// the named formatters below; a lint rule forbids ad-hoc `Intl.DateTimeFormat` / `dayjs().format` on
// content surfaces so the set of date styles stays closed. `locale` is required (no German default),
// so a missing locale is a compile error rather than a silently-German runtime string.
const formatWith = (value: string | Date, options: Intl.DateTimeFormatOptions, locale: string): string => {
  const date = toUtcDate(value);
  if (!date) return String(value);
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(date);
};

// `Todo.dueDate` and friends are LOCAL-midnight Dates; the UTC-pinned formatters below render them a day
// early east of UTC. Read LOCAL components so the calendar day survives.
export const toLocalIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

// "07.05.2026" / "05/07/2026" — numeric transaction date with year.
export const formatNumericDate = (value: string | Date, locale: string): string =>
  formatWith(value, { day: "2-digit", month: "2-digit", year: "numeric" }, locale);

// "07.05" / "05/07" — compact day + month for dense list badges, no year. German renders a year-less
// numeric date with a trailing ordinal dot ("07.05."); strip it so the badge doesn't read as truncated.
export const formatNumericDayMonth = (value: string | Date, locale: string): string =>
  formatWith(value, { day: "2-digit", month: "2-digit" }, locale).replace(/\.$/, "");

// "6. Juni 2026" / "June 6, 2026" — spelled day with year, locale-native punctuation.
export const formatDayMonthYear = (value: string | Date, locale: string): string =>
  formatWith(value, { day: "numeric", month: "long", year: "numeric" }, locale);

// "19. Juni" / "June 19" — spelled day + month, no year, for a label naming a chosen day.
export const formatDayMonthLong = (value: string | Date, locale: string): string =>
  formatWith(value, { day: "numeric", month: "long" }, locale);

// "5. Mär" / "Mar 5" — abbreviated day + month, no year, for dense recency labels.
export const formatDayMonth = (value: string | Date, locale: string): string =>
  formatWith(value, { day: "numeric", month: "short" }, locale);

// "5. Sept. 2026" / "Sep 5, 2026" — abbreviated day + month with year, for recency labels where a
// date years in the past must not read as this year.
export const formatDayShortMonthYear = (value: string | Date, locale: string): string =>
  formatWith(value, { day: "numeric", month: "short", year: "numeric" }, locale);

// "Freitag, 19. Juni 2026" / "Friday, Jun 19, 2026" — weekday + spelled day for the home greeting header.
export const formatWeekdayDate = (value: string | Date, locale: string): string =>
  formatWith(value, { weekday: "long", day: "numeric", month: "short", year: "numeric" }, locale);

// "Montag, 5. Januar 2026" / "Monday, January 5, 2026" — weekday with a spelled-out month, for
// headlines naming a single day.
export const formatWeekdayDateLong = (value: string | Date, locale: string): string =>
  formatWith(value, { weekday: "long", day: "numeric", month: "long", year: "numeric" }, locale);

// "Fr., 19. Juni" / "Fri, June 19" — abbreviated weekday naming a single day without a year,
// for compact sheet headers.
export const formatWeekdayShortDayMonth = (value: string | Date, locale: string): string =>
  formatWith(value, { weekday: "short", day: "numeric", month: "long" }, locale);

// "08:30" — backend LocalTime arrives as "HH:mm[:ss]"; the seconds never show.
export const formatHourMinute = (time: string): string => time.slice(0, 5);

// "Mai" / "May" — abbreviated month, for chart axes and the year-to-date sub-label.
export const formatMonth = (value: string | Date, locale: string): string =>
  formatWith(value, { month: "short" }, locale);

// "Januar 2026" / "January 2026" — spelled month with year, for headlines naming a single month.
export const formatMonthYear = (value: string | Date, locale: string): string =>
  formatWith(value, { month: "long", year: "numeric" }, locale);

// "vor 3 Tagen" / "gestern" — always relative. `numeric: "auto"` yields the today/yesterday wording.
export const formatRelativeDays = (value: string | Date, locale: string): string => {
  const date = toUtcDate(value);
  if (!date) return String(value);
  return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(-daysSince(value), "day");
};

const sharedYearRange = (
  range: { start: string; end: string },
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string => {
  const start = toUtcDate(range.start);
  const end = toUtcDate(range.end);
  if (!start || !end) return `${range.start} – ${range.end}`;

  const withYear = new Intl.DateTimeFormat(locale, { ...options, year: "numeric", timeZone: "UTC" });
  const startFmt =
    start.getUTCFullYear() === end.getUTCFullYear()
      ? new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" })
      : withYear;

  return `${startFmt.format(start)} – ${withYear.format(end)}`;
};

// "Oktober – Oktober 2025" / "Oktober 2025 – März 2026" — month-year span, year printed once when shared.
export const formatMonthYearRange = (range: { start: string; end: string }, locale: string): string =>
  sharedYearRange(range, locale, { month: "long" });

// "Apr. – Juli 2026" / "Juli 2025 – Juli 2026" — abbreviated variant for tight spots (period pill).
export const formatShortMonthYearRange = (range: { start: string; end: string }, locale: string): string =>
  sharedYearRange(range, locale, { month: "short" });

// "2. Feb. – 8. Feb. 2026" / "29. Dez. 2025 – 4. Jan. 2026" — day-precision span for headlines
// naming a single week, year printed once when shared.
export const formatDayMonthRange = (range: { start: string; end: string }, locale: string): string =>
  sharedYearRange(range, locale, { day: "numeric", month: "short" });

// "24.04. – 22.07.2026" / "01.11.2025 – 31.01.2026"
export const formatNumericDayMonthRange = (range: { start: string; end: string }, locale: string): string =>
  sharedYearRange(range, locale, { day: "2-digit", month: "2-digit" });

// "01.10.2025 – 30.10.2025" — explicit day-precision range (custom KPI period, period pill).
export const formatNumericDateRange = (range: { start: string; end: string }, locale: string): string => {
  const start = toUtcDate(range.start);
  const end = toUtcDate(range.end);
  if (!start || !end) return `${range.start} – ${range.end}`;
  return `${formatNumericDate(start, locale)} – ${formatNumericDate(end, locale)}`;
};

export const formatCurrency = (amount: number, currency: CurrencyCode, locale = DEFAULT_LOCALE): string =>
  formatCurrencyValue(amount, locale, currency, { maximumFractionDigits: 0 });

export const formatCurrencyDelta = (amount: number, currency: CurrencyCode, locale = DEFAULT_LOCALE): string =>
  formatCurrencyValue(amount, locale, currency, { signDisplay: "exceptZero", maximumFractionDigits: 0 });

export const formatPercentDelta = (value: number, locale = DEFAULT_LOCALE): string =>
  stripNbsp(
    new Intl.NumberFormat(locale, {
      style: "percent",
      signDisplay: "exceptZero",
      maximumFractionDigits: 0,
    }).format(value / 100),
  );

// Compact magnitude label for dense axes: "1.2M", "3k", "850".
export const formatCompact = (value: number): string => {
  const abs = Math.abs(value);
  const trim = (scaled: number) => Number(scaled.toFixed(1)).toString();

  if (abs >= 1_000_000) return `${trim(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${trim(value / 1_000)}k`;

  return trim(value);
};

/** formatCompact with the currency symbol placed and spaced by the locale's currencyPattern: "15k€", "$15k", "-$15k", "1.2M XAU". */
export const formatCompactCurrency = (value: number, currency: CurrencyCode, locale = DEFAULT_LOCALE): string => {
  const { symbol, symbolFirst, spaced } = currencyPattern(locale, currency);
  const gap = spaced ? " " : "";
  const sign = value < 0 ? "-" : "";
  const amount = formatCompact(Math.abs(value));
  return symbolFirst ? `${sign}${symbol}${gap}${amount}` : `${sign}${amount}${gap}${symbol}`;
};

// Sign and magnitude of a margin figure must be read off the same rounding, or a delta that
// displays as zero still picks up a "+".
const marginPercentagePoints = (ratio: number): number => {
  const rounded = Math.round(ratio * 1000) / 10;
  return Object.is(rounded, -0) ? 0 : rounded;
};

// Margin as a one-decimal percentage from a 0..1 ratio ("12.5%"), matching the management view.
export const formatMargin = (ratio: number, locale = DEFAULT_LOCALE): string =>
  `${marginPercentagePoints(ratio).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

// Whole-number percentage from a 0..1 ratio: 0.4 → "40 %".
export const formatCompactPercent = (ratio: number, locale = DEFAULT_LOCALE): string =>
  new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(ratio);

/**
 * Profit annotated with its margin ratio: "€1,234 (12.5%)". This is how the platform presents the
 * "Marge" metric (see the management view); the headline figure is profit, the percentage is margin.
 */
export const formatProfitWithMargin = (
  profit: number,
  marginRatio: number | null,
  currency: CurrencyCode,
  locale = DEFAULT_LOCALE,
): string => {
  const profitStr = formatCurrency(profit, currency, locale);
  return marginRatio != null ? `${profitStr} (${formatMargin(marginRatio, locale)})` : profitStr;
};

/**
 * Formats a KPI value in the unit the KPI is measured in: currency for revenue and profit,
 * a percentage for margin (the backend delivers it as a 0..1 ratio), a plain count for orders.
 *
 * @param compact - Shortens large numbers (e.g. for chart axes); ignored for margin.
 */
export const formatKpiValue = (
  kpi: Kpi,
  value: number,
  currency: CurrencyCode,
  locale = DEFAULT_LOCALE,
  compact = false,
): string => {
  switch (kpi) {
    case Kpi.REVENUE:
    case Kpi.PROFIT:
      return compact ? formatCompact(value) : formatCurrency(value, currency, locale);
    case Kpi.MARGIN:
      return formatMargin(value, locale);
    case Kpi.ORDERS:
      return compact ? formatCompact(value) : new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
  }
};

// "12" — whole minutes for an exact (routed) driving time; units live in the ICU message.
export const formatMinutesValue = (minutes: number, locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(minutes);

// "8-10" — whole-minute estimate range, hyphenated per the route-planning screens.
export const formatMinutesRange = (min: number, max: number, locale: string): string =>
  `${formatMinutesValue(min, locale)}-${formatMinutesValue(max, locale)}`;

// "8,4" / "8.4" — one-decimal distance for an exact (routed) value.
export const formatKmValue = (km: number, locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(km);

// "2-5" — whole-km estimate range. Bounds round outward (floor/ceil) so the displayed
// range always contains the computed one; decimals would fake precision an estimate lacks.
export const formatKmRange = (min: number, max: number, locale: string): string => {
  const wholeKm = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  return `${wholeKm.format(Math.floor(min))}-${wholeKm.format(Math.ceil(max))}`;
};

/** Signed counterpart of [formatKpiValue]: a delta in the unit the KPI is measured in. */
export const formatKpiDelta = (kpi: Kpi, value: number, currency: CurrencyCode, locale = DEFAULT_LOCALE): string => {
  switch (kpi) {
    case Kpi.REVENUE:
    case Kpi.PROFIT:
      return formatCurrencyDelta(value, currency, locale);
    case Kpi.MARGIN: {
      const formatted = formatMargin(value, locale);
      return marginPercentagePoints(value) > 0 ? `+${formatted}` : formatted;
    }
    case Kpi.ORDERS:
      return new Intl.NumberFormat(locale, { signDisplay: "exceptZero", maximumFractionDigits: 0 }).format(value);
  }
};
